import React from 'react';
import {assignProps} from '@sail/engine';
import type {PopoverProps, TooltipLayerProps} from '@sail/ui';
import {
  ButtonConfig,
  CompositeFieldConfig,
  DateFieldConfig,
  DialogConfig,
  DrawerConfig,
  PhoneNumberFieldConfig,
  SelectConfig,
  PopoverConfig,
  TooltipConfig,
  TooltipLayerConfig,
  TextFieldConfig,
  css,
  tokens,
  useIsWithinListBoxContext,
  view,
} from '@sail/ui';
import {FormBlockConfig} from '@stripe-internal/ark-lib';
import {message, useIntl} from '@sail/intl';
import {Button} from '@stripe-internal/connect-embedded-lib';
import {ARKAddressFieldConfig} from '@stripe-internal/ark';

import {useFlag} from '@sail/access';
import {BottomDialog} from '../../components/SharedComponents/Mobile/BottomDialog';
import {useShouldRenderMobileUI} from './mobileUtils';
import type {ConnectElementImportKeys} from '../../connect/ConnectJSInterface/ConnectElementList';

const messages = {
  tooltipCloseButton: message({
    id: 'connectEmbed.tooltip.closeButton',
    description: 'Button to close the Tooltip component',
    defaultMessage: 'Close',
  }),
};

const useMobilePopover = (props: PopoverProps) => {
  const [open, setOpen] = React.useState(true);

  // If the Popover is not entirely unmounted, we need to listen for it re-opening, like when rendering the `Menu` component
  React.useEffect(() => {
    if (props.open) {
      setOpen(true);
    }
  }, [props.open]);

  const {onClose, title} = props;

  const handleClose = React.useCallback(() => {
    setOpen(false);

    // Will properly close the hidden Popover
    onClose?.();
  }, [onClose]);

  // We don't override popovers when they are being used to display listboxes, like search results
  if (useIsWithinListBoxContext()) {
    return {};
  }

  return {
    style: {display: 'none'},
    children: (
      <>
        {open && (
          <BottomDialog handleClose={handleClose} title={title}>
            <view.div css={{width: 'fill'}}>{props.children}</view.div>
          </BottomDialog>
        )}
      </>
    ),
  };
};

const useMobileTooltip = (props: TooltipLayerProps) => {
  const [open, setOpen] = React.useState(true);
  const intl = useIntl();
  const triggerRef = props.triggerRef.current;

  // If the tooltip is nested in an Overlay, the triggerElement will receive a blur event once the BottomDialog renders which we need to ignore
  React.useEffect(() => {
    if (triggerRef) {
      triggerRef.addEventListener(
        'blur',
        (e) => {
          e.stopPropagation();
        },
        true,
      );
    }

    return () => {
      if (triggerRef) {
        triggerRef.removeEventListener(
          'blur',
          (e) => e.stopPropagation(),
          true,
        );
      }
    };
  }, [triggerRef]);

  const handleClose = React.useCallback(() => {
    setOpen(false);
    // Manually trigger a mouse leave event on the trigger element to close the hidden tooltip
    if (triggerRef) {
      triggerRef.dispatchEvent(
        new MouseEvent('mouseleave', {
          screenX: 0,
          screenY: 0,
        }),
      );
    }
  }, [triggerRef]);

  return {
    style: {display: 'none'},
    children: (
      <>
        {open && (
          <BottomDialog
            handleClose={handleClose}
            footer={
              <Button onPress={handleClose} type="primary" maximized>
                {intl.formatMessage(messages.tooltipCloseButton)}
              </Button>
            }
          >
            <view.div
              css={{
                padding: 'space.250',
                color: 'subdued',
                font: 'body.small',
                width: 'fill',
              }}
            >
              {props.children}
            </view.div>
          </BottomDialog>
        )}
      </>
    ),
  };
};

const MobileUIOverrides = ({
  children,
  component,
  isMobileSdk,
}: {
  children: React.ReactNode;
  component: ConnectElementImportKeys;
  isMobileSdk: boolean;
}) => {
  const showMobilePopoverTooltip = useShouldRenderMobileUI(component);
  const enableSailBottomSheets = useFlag('enable_sail_bottom_sheets');

  const overlayCSS = css({
    // This is an incomplete bottom sheet solution that works well enough for preview and ideally, a proper bottom sheet implementation will follow from Sail
    // We have to re-apply the safe area values even though we apply then in the webview.html files as these Dialogs are full-screen
    position: 'fixed',
    marginTop: 'calc(env(safe-area-inset-top, 0) + 40px)',
    maxHeight: 'calc(100% - calc(env(safe-area-inset-top, 0) + 40px))',
    height: 'auto',
    bottom: 'space.0',
    top: 'unset',

    borderTopLeftRadius: tokens.space[150],
    borderTopRightRadius: tokens.space[150],

    paddingBottom: 'env(safe-area-inset-bottom, 0)',
  });

  const sharedMobileOverrides = [
    TooltipConfig.customize({uses: [assignProps({delay: 0})]}),
    ButtonConfig.customize({
      uses: [
        assignProps(isMobileSdk ? {size: 'large'} : {}),
        css({'-webkit-tap-highlight-color': 'transparent'}),
      ],
    }),
    ...(isMobileSdk
      ? [
          ARKAddressFieldConfig.customize({
            uses: [assignProps({gap: 'small', size: 'large'})],
          }),
          FormBlockConfig.customize({uses: [assignProps({gap: 'xlarge'})]}),
          TextFieldConfig.customize({uses: [assignProps({size: 'large'})]}),
          DateFieldConfig.customize({uses: [assignProps({size: 'large'})]}),
          SelectConfig.customize({uses: [assignProps({size: 'large'})]}),
          PhoneNumberFieldConfig.customize({
            uses: [assignProps({size: 'large'})],
          }),
          CompositeFieldConfig.customize({
            uses: [assignProps({size: 'large'})],
          }),
        ]
      : []),
  ];

  // Sail bottom sheet overrides trump everything else. When the flag is On,
  // we want Sail bottom sheets to always be used instead of Connect bottom sheets.
  // If not, we honor showMobilePopoverTooltip to maintain the previous behavior
  // of Connect mobile overrides.
  // TODO: Remove this once we are fully migrated to Sail bottom sheets.
  if (enableSailBottomSheets) {
    return (
      <view.div
        uses={[
          ...sharedMobileOverrides,
          TooltipLayerConfig.customize({uses: [assignProps(useMobileTooltip)]}),
          DialogConfig.customize({
            uses: [assignProps({preferredMobileDisplay: 'bottom-sheet'})],
          }),
          DrawerConfig.customize({
            uses: [assignProps({preferredMobileDisplay: 'bottom-sheet'})],
          }),
        ]}
      >
        {children}
      </view.div>
    );
  } else if (showMobilePopoverTooltip) {
    return (
      <view.div
        uses={[
          ...sharedMobileOverrides,
          PopoverConfig.customize({uses: [assignProps(useMobilePopover)]}),
          DialogConfig.customize({uses: [overlayCSS]}),
          DrawerConfig.customize({uses: [overlayCSS]}),
          TooltipLayerConfig.customize({uses: [assignProps(useMobileTooltip)]}),
        ]}
      >
        {children}
      </view.div>
    );
  } else {
    return <>{children}</>;
  }
};

export default MobileUIOverrides;
